/**
 * Where the state's money goes in 2026: the approved state budget regrouped by purpose.
 *
 * Source: Valtion talousarvio 2026, "Määrärahat pääluokittain vuosina 2024-2026" and the general
 * justifications (budjetti.vm.fi, hallituksen esitys, https://budjetti.vm.fi/sisalto.jsp?year=2026&lang=fi&maindoc=/2026/tae/hallituksenEsitys/hallituksenEsitys.xml,
 * read 2026-09-06). Total appropriations EUR 90,306 million. The Ministry of Finance main class
 * (EUR 41,840 million) is mostly money passed on, so it is split using the amounts the general
 * justifications state: wellbeing services counties EUR 27.1 billion, municipal basic-services
 * grant EUR 3.6 billion plus tax-loss compensation EUR 0.5 billion, EU contributions EUR 2.8
 * billion, state pensions EUR 6.1 billion; the rest of that class is administration (tax
 * administration, customs, statistics, state property). Main classes used as they are:
 * defence 6,297; education and culture 8,934; transport 3,849; employment and economy 3,349;
 * debt interest 3,246; agriculture and forestry 2,553; interior 2,090 + justice 1,243; foreign
 * affairs 1,155; environment 494; social affairs and health 14,840 (Kela benefits, state share of
 * national pensions and other social security; not the counties, which are funded from the
 * Ministry of Finance class); Parliament 148, President 29, Prime Minister's Office 239.
 */
export type StateSpendingKey =
  | 'healthSocialCounties'
  | 'socialSecurity'
  | 'municipalGrants'
  | 'education'
  | 'defence'
  | 'transport'
  | 'employmentEconomy'
  | 'debtInterest'
  | 'eu'
  | 'agriculture'
  | 'securityJustice'
  | 'foreignAffairs'
  | 'environment'
  | 'administrationOther';

/** EUR million, 2026. */
export const STATE_BUDGET_2026_EUR_M: Record<StateSpendingKey, number> = {
  healthSocialCounties: 27100,
  socialSecurity: 14840 + 6100,
  municipalGrants: 3600 + 500,
  education: 8934,
  defence: 6297,
  transport: 3849,
  employmentEconomy: 3349,
  debtInterest: 3246,
  eu: 2800,
  agriculture: 2553,
  securityJustice: 2090 + 1243,
  foreignAffairs: 1155,
  environment: 494,
  administrationOther: 0, // filled below as the remainder of the 90,306 total
};
export const STATE_BUDGET_2026_TOTAL_EUR_M = 90306;
STATE_BUDGET_2026_EUR_M.administrationOther =
  STATE_BUDGET_2026_TOTAL_EUR_M - Object.entries(STATE_BUDGET_2026_EUR_M).reduce((s, [k, v]) => (k === 'administrationOther' ? s : s + v), 0);

export const STATE_SPENDING_KEYS = Object.keys(STATE_BUDGET_2026_EUR_M) as StateSpendingKey[];

/** Share of each purpose in the 2026 budget, %. */
export function stateSpendingProfile(): Record<StateSpendingKey, number> {
  return Object.fromEntries(STATE_SPENDING_KEYS.map((k) => [k, (STATE_BUDGET_2026_EUR_M[k] / STATE_BUDGET_2026_TOTAL_EUR_M) * 100])) as Record<StateSpendingKey, number>;
}
